import { useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';
import {
  AdminHistoryTypes,
  AdminHistoryTypesList,
  CheckHistoryTypes,
} from 'constants/check-history-types';
import { now } from 'helpers/date';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { useApiUploadActions } from 'hooks/upload-actions';
import { useSupportUserNames } from 'hooks/use-support-user-names';
import moment from 'moment';
import { Badge } from 'react-bootstrap';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';

import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import Alert from 'components/Alert/Alert';
import {
  CollapsibleBlock,
  Variant as CollapsibleBlockVariant,
} from 'components/CollapsibleBlock/CollapsibleBlock';
import { CommentBox } from 'components/CommentBox/CommentBox';
import { Comments } from 'components/Comments/Comments';
import {
  FormMessage,
  MessageType,
  MultipleSelect,
  Select,
  ToggleSwitch,
} from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { IconButton } from 'components/IconButton/IconButton';
import { KeyValueModal } from 'components/KeyValueModal/KeyValueModal';
import { StatusPill } from 'components/Pills/StatusPills';
import { RowDetails } from 'components/RowDetails/RowDetails';
import { CustomToast } from 'components/Toast/CustomToast';

import {
  useCancelCandidateCheckMutation,
  useCreateCheckHistoryMutation,
  useDeleteCheckHistoryMutation,
  useGenerateReportMutation,
  useGenerateSubchecksCombinedReportMutation,
  useLazyGetProviderFormQuery,
  useRegenerateSubChecksMutation,
  useRestoreCandidateCheckMutation,
  useSendPartnerWebhookMutation,
  useSendToProviderMutation,
  useUpdateCandidateCheckMutation,
  useUpdateCheckHistoryMutation,
  useUpdateCheckSupportUserMutation,
  useUpdateStatusMutation,
} from 'api/candidate-checks-api';
import { useGetCandidateChecksValuesQuery } from 'api/candidates-api';
import { useCreateDocumentRequestMutation } from 'api/document-requests-api';
import {
  useCreateSubCheckMutation,
  useDeleteSubCheckMutation,
  useUpdateSubCheckMutation,
} from 'api/sub-checks-api';

import { CandidateCompletionModal } from '../CandidateCompletionModal/CandidateCompletionModal';
import { CandidateCheckDocumentUploadModal } from '../CandidateDocumentUpload/CandidateCheckDocumentUploadModal';
import { DocumentRequestModal } from '../DocumentRequestModal/DocumentRequestModal';
import { NewSubCheckModal } from '../NewSubCheckModal/NewSubCheckModal';
import styles from './CandidateCheckDetails.module.scss';

export const CandidateCheckDetails = ({
  candidate,
  company = null,
  check,
  checkType,
  companyChecks = [],
  referees = [],
  checkNotes = [],
  checkValues = {},
  setLoading = () => {},
  addOrUpdateCheck = () => {},
  onDocumentUpload = () => {},
  refetchSupportNotes = () => {},
  supportUploads = [],
  resultUploads = [],
}) => {
  const { t } = useTranslation();
  const [getProviderForm] = useLazyGetProviderFormQuery();
  const [sendToProvider] = useSendToProviderMutation();
  const [updateCandidateCheck] = useUpdateCandidateCheckMutation();
  const [cancelCandidateCheck] = useCancelCandidateCheckMutation();
  const [generateReport] = useGenerateReportMutation();
  const [generateSubchecksCombinedReport] = useGenerateSubchecksCombinedReportMutation();
  const [regenerateSubChecks] = useRegenerateSubChecksMutation();
  const [updateSubCheck] = useUpdateSubCheckMutation();
  const [createSubCheck] = useCreateSubCheckMutation();
  const [deleteSubCheck] = useDeleteSubCheckMutation();
  const [createCheckHistory] = useCreateCheckHistoryMutation();
  const [updateCheckHistory] = useUpdateCheckHistoryMutation();
  const [deleteCheckHistory] = useDeleteCheckHistoryMutation();
  const [restoreCandidateCheck] = useRestoreCandidateCheckMutation();
  const [sendPartnerWebhook] = useSendPartnerWebhookMutation();
  const [updateStatus] = useUpdateStatusMutation();
  const [updateCheckSupportUser] = useUpdateCheckSupportUserMutation();
  const [createDocumentRequest] = useCreateDocumentRequestMutation();
  const [showClientInputValues, setShowClientInputValues] = useState(false);
  const [showExtraDetails, setShowExtraDetails] = useState(false);
  const [cancelConfirmation, setCancelConfirmation] = useState(false);
  const [completionModal, setCompletionModal] = useState(false);
  const [processTooltip, setProcessTooltip] = useState('');
  const [restoreConfirmation, setRestoreConfirmation] = useState(false);
  const [showNewSubCheckModal, setShowNewSubCheckModal] = useState(false);
  const [showDocumentRequestModal, setShowDocumentRequestModal] = useState(false);
  const { deleteUpload, updateUpload } = useApiUploadActions();
  const [showCheckDocumentUploadModal, setShowCheckDocumentUploadModal] = useState(false);
  const { supportUsers } = useSupportUserNames();
  const [supportNotes, setSupportNotes] = useState(checkNotes);
  const [selectedSupportUsers, setSelectedSupportUsers] = useState(check?.support_user_ids || []);
  const commentBoxRef = useRef(null);

  // Helper function to check if upload metadata matches document request config
  const uploadMatchesDocumentRequest = useMemo(() => {
    if (!checkType?.document_request_config) {
      return false;
    }

    // Get upload metadata from config, or default to check_type
    const requestConfig = checkType.document_request_config;
    const uploadMetadata = requestConfig.upload_metadata || {
      check_type: checkType?.slug,
    };

    // Check all uploads (support and result) for matching metadata
    const allUploads = [...supportUploads, ...resultUploads];

    return allUploads.some(upload => {
      if (!upload?.metadata) {
        return false;
      }

      // Check if all key-value pairs in uploadMetadata match the upload's metadata
      return Object.entries(uploadMetadata).every(([key, value]) => {
        return upload.metadata[key] === value;
      });
    });
  }, [checkType?.document_request_config, checkType?.slug, supportUploads, resultUploads]);

  // Evaluate if pause toggle should be shown
  const showPauseToggle = useMemo(() => {
    // Only show toggle if:
    // 1. Not a sub-check (handled by modal)
    // 2. Document request config exists
    // 3. no_followup is not enabled (if enabled, no automated reminders so no need to pause)
    // 4. Conditions are met (or no conditions)
    // 5. No document has been uploaded yet
    if (uploadMatchesDocumentRequest) {
      return false;
    }

    const requestConfig = checkType?.document_request_config;
    if (!requestConfig) {
      return false;
    }

    // Don't show toggle if no_followup is enabled (no automated reminders to pause)
    if (requestConfig?.no_followup === true) {
      return false;
    }

    const conditions = requestConfig.conditions;

    // If no conditions, document requests apply to all
    if (!conditions || (Array.isArray(conditions) && conditions.length === 0)) {
      return true;
    }

    // Merge check details and candidate values (same as backend does)
    const details = check?.details || {};
    const candidateValues = checkValues?.values || {};
    const values = { ...details, ...candidateValues };

    // Evaluate conditions using the same helper as other form fields
    return evaluateFieldConditions(conditions, values);
  }, [
    checkType?.document_request_config,
    check?.details,
    check?.upload_id,
    checkValues?.values,
    uploadMatchesDocumentRequest,
  ]);

  const { data: checkValuesData } = useGetCandidateChecksValuesQuery(check?.candidate_id, {
    skip: !check?.candidate_id,
  });

  // Calculate age and check if below minimum age
  const ageWarning = useMemo(() => {
    if (!checkType?.min_age || !checkValuesData?.check_values?.values) {
      return null;
    }

    const dob = checkValuesData.check_values.values.dob;
    if (!dob) {
      return null;
    }

    // Parse DOB (format is d/m/Y based on backend)
    const dobMoment = moment(dob, 'DD/MM/YYYY');
    if (!dobMoment.isValid()) {
      return null;
    }

    const age = moment().diff(dobMoment, 'years');
    if (age < checkType.min_age) {
      return `The candidate is ${age} years old, which is below the minimum age requirement of ${checkType.min_age} years for this check type.`;
    }

    return null;
  }, [checkType?.min_age, checkValuesData?.check_values?.values]);

  // Short support note (e.g. PV: "Bot Launched with AU option") from extra_data (set by bot via queue)
  const significantNote = useMemo(() => {
    const note = check?.extra_data?.significant_note;
    return typeof note === 'string' && note.trim() !== '' ? note.trim() : null;
  }, [check?.extra_data?.significant_note]);

  // Get company check configuration values
  const companyCheckConfig = useMemo(() => {
    const companyCheck = companyChecks?.find(cc => cc.type === checkType?.slug);
    return companyCheck?.configuration || {};
  }, [companyChecks, checkType?.slug]);

  // Get client-specific instructions from company check configuration (string or object from API)
  const clientSpecificInstructions = useMemo(() => {
    const raw = companyCheckConfig?.client_specific_instructions;
    if (raw == null) return null;
    const text =
      typeof raw === 'string'
        ? raw
        : typeof raw === 'object' && raw !== null && typeof raw.value === 'string'
          ? raw.value
          : String(raw);
    return text.trim() !== '' ? text : null;
  }, [companyCheckConfig]);

  // Check if alternative_report is enabled
  const hasAlternativeReport = useMemo(() => {
    return companyCheckConfig?.alternative_report === true;
  }, [companyCheckConfig]);

  // Check if we should show the processing info message
  const showProcessingInfo = useMemo(() => {
    return hasAlternativeReport || !!clientSpecificInstructions;
  }, [hasAlternativeReport, clientSpecificInstructions]);

  // Set check params
  useEffect(() => {
    // Process tooltip
    if (check.bot_message) setProcessTooltip(check.bot_message);
    else if (check.job_at) setProcessTooltip(t('admin.candidate.check.waitingOnProvider'));
    else setProcessTooltip(t('admin.candidate.check.waitingToBeLaunched'));
  }, [check]);

  useEffect(() => {
    setSupportNotes(checkNotes);
  }, [checkNotes]);

  useEffect(() => {
    setSelectedSupportUsers(check?.support_user_ids || []);
  }, [check?.support_user_ids]);

  // Launch process
  const handleCheckProcess = async () => {
    setLoading(true);
    const { data: result } = await sendToProvider(check.id);
    addOrUpdateCheck(result);
    setLoading(false);
  };

  // Set in progress
  const setInProgress = async () => {
    handleUpdate({ job_at: now() });
  };

  const removeComplete = async () => {
    setLoading(true);
    const { data: result } = await updateStatus({ id: check.id, data: { completed_at: null } });
    if (check?.upload_id) {
      await deleteUpload(check.upload_id);
    }
    addOrUpdateCheck(result);
    setLoading(false);
  };

  const handleSendPartnerWebhook = async () => {
    setLoading(true);
    await sendPartnerWebhook(check.id);
    setLoading(false);
  };

  // Update check
  const handleUpdate = async (data, generateReport = false) => {
    setLoading(true);
    const { data: result } = await updateCandidateCheck({ id: check.id, data });
    addOrUpdateCheck(result);
    if (generateReport) {
      await handleGenerateReport();
    }
    onDocumentUpload();
    setLoading(false);
  };

  // Download consent form (wetSignature = true for wet signature version)
  const handleDownloadProviderForm = async (wetSignature = false) => {
    setLoading(true);
    await getProviderForm({ check, candidate, wetSignature });
    setLoading(false);
  };

  // Cancel
  const handleCancel = async () => {
    setCancelConfirmation(false);
    setLoading(true);
    const { data: result } = await cancelCandidateCheck(check.id);
    addOrUpdateCheck(result);
    setLoading(false);
  };

  // Restore
  const handleRestore = async () => {
    setRestoreConfirmation(false);
    setLoading(true);
    const { data: result } = await restoreCandidateCheck(check.id);
    addOrUpdateCheck(result);
    setLoading(false);
  };

  // Generate report
  const handleGenerateReport = async (data = {}, skipAttachments = false) => {
    setLoading(true);
    const { data: updatedCheck } = await generateReport({
      id: check.id,
      data: {
        ...data,
        ...(skipAttachments ? { skip_attachments: true } : {}),
      },
    });
    addOrUpdateCheck(updatedCheck);
    setLoading(false);
  };

  // Generate sub-checks combined report
  const handleGenerateSubchecksCombinedReport = async () => {
    setLoading(true);
    const { data: result } = await generateSubchecksCombinedReport(check.id);
    addOrUpdateCheck(result);
    setLoading(false);
  };

  // Regenerate sub-checks
  const handleRegenerateSubChecks = async () => {
    setLoading(true);
    const { data: result } = await regenerateSubChecks(check.id);
    addOrUpdateCheck(result);
    setLoading(false);
  };

  // Create sub-check
  const handleCreateSubCheck = async subCheckData => {
    setLoading(true);
    setShowNewSubCheckModal(false);
    const { data: newSubCheck } = await createSubCheck(subCheckData);
    addOrUpdateCheck({ ...check, sub_checks: [...check.sub_checks, newSubCheck] });
    setLoading(false);
  };

  const getSubCheck = useMemo(
    () => subCheck => {
      if (checkType?.slug !== 'custom_reference') return subCheck;

      const companyCheck = companyChecks.find(cc => cc.type === checkType.slug);

      const formUrl = companyCheck?.configuration?.use_new_form
        ? `${process.env.REACT_APP_WEB_URL}/form_submission/new_referee/`
        : `${process.env.REACT_APP_WEB_URL}/form_submission/referee/`;

      if (subCheck?.referee_id) {
        const referee = referees.find(referee => referee.id === subCheck.referee_id);
        return {
          ...subCheck,
          url: `${formUrl}${referee?.token}`,
        };
      }
      return subCheck;
    },
    [referees, companyChecks],
  );

  const handleUpdateSupportUser = async users => {
    // Update local state immediately for UI responsiveness
    setSelectedSupportUsers(users || []);

    setLoading(true);
    try {
      const { data: result } = await updateCheckSupportUser({
        id: check.id,
        data: { support_user_ids: users },
      });
      addOrUpdateCheck(result);

      if (result.error) {
        throw new Error(
          result.error.data?.message || result.error.message || 'Failed to update support user',
        );
      }

      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Nicely done - Support user updated"
            message={`Support user updated successfully for ${candidate.first_name} ${candidate.last_name}.`}
            type="success"
          />
        ),
        {
          duration: 5000,
        },
      );
    } catch (error) {
      // Revert local state on error
      setSelectedSupportUsers(check?.support_user_ids || []);
      toast.custom(
        t => (
          <CustomToast
            t={t}
            title={`Oops - ${error}`}
            message={`Please try again or if it continues, please contact the development team.`}
            type="error"
          />
        ),
        {
          duration: 5000,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCheckHistory = async ({
    message,
    sub_check_id,
    type = CheckHistoryTypes.AdminGeneral,
  }) => {
    setLoading(true);
    try {
      const result = await createCheckHistory({
        id: check?.id,
        data: {
          candidate_id: candidate?.id,
          message: message,
          sub_check_id: sub_check_id || null,
          type: type,
          resolved: type === CheckHistoryTypes.AdminGeneral,
        },
      });

      if (result.error) {
        throw new Error(result.error.data?.message || result.error.message || 'Failed to add note');
      }

      toast.custom(t => <CustomToast t={t} title="Note posted — good work!" type="success" />, {
        duration: 5000,
      });

      if (commentBoxRef.current?.reset) {
        commentBoxRef.current.reset();
      }

      refetchSupportNotes();
    } catch (error) {
      toast.custom(
        t => (
          <CustomToast
            t={t}
            title={`Oops - ${error.message || error}`}
            message={`Please try again or if it continues, please contact the development team.`}
            type="error"
          />
        ),
        {
          duration: 5000,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCheckHistory = async ({ id }) => {
    setLoading(true);
    try {
      const result = await deleteCheckHistory({ id: check.id, check_history_id: id });
      if (result.error) {
        throw new Error(
          result.error.data?.message || result.error.message || 'Failed to delete note',
        );
      }
      refetchSupportNotes();
      toast.custom(t => <CustomToast t={t} title="Note deleted — good work!" type="success" />, {
        duration: 5000,
      });
    } catch (error) {
      toast.custom(
        t => <CustomToast t={t} title={`Oops - ${error.message || error}`} type="error" />,
        {
          duration: 5000,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCheckHistory = async (data, id) => {
    setLoading(true);
    try {
      const result = await updateCheckHistory({
        id: check.id,
        check_history_id: id,
        data: {
          ...(data.message && { message: data.message }),
          ...data,
          resolved: data?.type === CheckHistoryTypes.AdminGeneral ? true : data?.resolved || false,
        },
      });
      if (result.error) {
        throw new Error(
          result.error.data?.message || result.error.message || 'Failed to update note',
        );
      }
      refetchSupportNotes();
      toast.custom(t => <CustomToast t={t} title="Note updated — good work!" type="success" />, {
        duration: 5000,
      });
    } catch (error) {
      toast.custom(
        t => <CustomToast t={t} title={`Oops - ${error.message || error}`} type="error" />,
        {
          duration: 5000,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDocumentRequest = async data => {
    setLoading(true);
    try {
      await createDocumentRequest({
        checkId: check.id,
        data,
      }).unwrap();

      setShowDocumentRequestModal(false);
      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Document request sent!"
            message={`Document request for "${data.document_name}" has been sent to the candidate.`}
            type="success"
          />
        ),
        {
          duration: 5000,
        },
      );
      return { success: true };
    } catch (error) {
      const errorMessage = error.data?.message || error.message || 'Unknown error occurred';
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const handleBlockResultUpload = async () => {
    setLoading(true);
    await updateUpload({ id: check?.upload_id, blocked: true });
    addOrUpdateCheck({ ...check, result_upload: { ...check?.result_upload, blocked: true } });
    toast.custom(
      t => (
        <CustomToast
          t={t}
          title="Result upload blocked!"
          message="Result upload has been blocked and won't be visible to the candidate."
          type="success"
        />
      ),
      { duration: 5000 },
    );
    setLoading(false);
  };

  return (
    <div
      className={cn(
        'card',
        'card-with-border',
        'u-width-100',
        'u-padding--small',
        'position-relative',
        {
          [styles.disabled]: check.dummy,
        },
      )}>
      {check.details && (
        <KeyValueModal
          visible={showClientInputValues}
          onClose={() => setShowClientInputValues(false)}
          title={t('admin.candidate.check.clientInputValues')}
          data={check.details}
        />
      )}

      {check.extra_data && (
        <KeyValueModal
          visible={showExtraDetails}
          onClose={() => setShowExtraDetails(false)}
          title={t('admin.candidate.check.moreDetails')}
          data={check.extra_data}
        />
      )}

      <NewSubCheckModal
        check={check}
        visible={showNewSubCheckModal}
        onCreate={handleCreateSubCheck}
        onClose={() => setShowNewSubCheckModal(false)}
      />

      <Alert
        show={cancelConfirmation}
        title={t('admin.candidate.check.cancel.confirmation', { title: checkType?.title })}
        ok={t('common.yes')}
        cancel={t('common.no')}
        onOk={handleCancel}
        onCancel={() => setCancelConfirmation(false)}
      />

      <Alert
        show={restoreConfirmation}
        title={t('admin.candidate.check.restore.confirmation', { title: checkType?.title })}
        ok={t('common.yes')}
        cancel={t('common.no')}
        onOk={handleRestore}
        onCancel={() => setRestoreConfirmation(false)}
      />

      <CandidateCompletionModal
        companyId={candidate?.company_id}
        company={company}
        check={check}
        checkType={checkType}
        companyCheck={companyChecks?.find(cc => cc.type === check.type)}
        candidate={candidate}
        candidateValues={checkValues?.values}
        visible={completionModal}
        onUpdate={handleUpdate}
        onClose={() => setCompletionModal(false)}
        showPauseToggle={showPauseToggle}
      />

      <DocumentRequestModal
        visible={showDocumentRequestModal}
        check={check}
        checkType={checkType}
        onSubmit={handleCreateDocumentRequest}
        onClose={() => setShowDocumentRequestModal(false)}
      />

      {checkType?.is_manual && check.submitted_at && !check.completed_at && (
        <OverlayTrigger placement="top" overlay={<Tooltip>{processTooltip}</Tooltip>}>
          <div
            className={cn(styles.adminState, {
              [styles.pending]: check.job_at,
              [styles.error]: check.bot_message,
            })}></div>
        </OverlayTrigger>
      )}
      <div className="d-flex flex-column u-gap-medium">
        <div
          className={cn(
            'd-flex',
            'justify-content-between',
            'align-items-start',
            'u-margin-top--small',
          )}>
          <div className="d-flex align-items-center u-gap flex-wrap">
            <Badge className={`u-status-${check.status}`}>{check.status}</Badge>
            {check?.upload_id && (
              <StatusPill
                text={`Result Uploaded ID: ${check.upload_id}`}
                status={'success'}
                className={styles.status}
              />
            )}
            {check?.completed_at && !check?.upload_id && checkType?.result_type !== 'link' && (
              <StatusPill text={`No Result Uploaded`} status={'error'} className={styles.status} />
            )}
            {check?.job_at && check?.overdue && (
              <StatusPill text={`Provider overdue`} status={'error'} className={styles.status} />
            )}
          </div>
          <div className={cn('d-flex')}>
            <IconButton
              prefix="fa"
              className={cn('u-margin-right', {
                [styles.processSuccedd]: check.job_at && !check.completed_at,
                [styles.processFailed]: check.bot_message && !check.completed_at,
              })}
              icon="rocket"
              tip={t('admin.candidate.check.launchBackgroundProcess')}
              disabled={
                !checkType?.has_job ||
                check?.canceled ||
                check?.provider_id ||
                (check?.status !== 'in-progress' && check?.status !== 'submitted')
              }
              onClick={handleCheckProcess}
            />
            {check.status === 'in-progress' && !checkType?.has_subchecks && (
              <IconButton
                prefix="fa"
                className="u-margin-right"
                icon="envelope"
                tip={t('admin.candidate.documentRequest.title')}
                onClick={() => setShowDocumentRequestModal(true)}
              />
            )}
            <IconButton
              prefix="fa"
              className="u-margin-right"
              icon="spinner"
              tip={t('admin.candidate.check.setInProgress')}
              disabled={check.job_at || !check.submitted_at || check.completed_at}
              onClick={setInProgress}
            />
            {check.completed_at && (
              <IconButton
                prefix="fa"
                className="u-margin-right"
                icon="undo"
                tip={t('common.undoCompletion')}
                onClick={removeComplete}
              />
            )}
            <IconButton
              prefix="fa"
              className="u-margin-right"
              icon="check"
              tip={t('common.complete')}
              disabled={check.cancel || !check.submitted_at || checkType?.has_subchecks}
              onClick={() => setCompletionModal(true)}
            />
            <ActionsMenu>
              <ActionsMenu.Item
                icon="file"
                title={t('admin.candidate.check.clientInputValues')}
                disabled={!check?.details || check?.details?.length === 0}
                onClick={() => setShowClientInputValues(true)}
              />
              <ActionsMenu.Item
                icon="file-text"
                title={t('admin.candidate.check.moreDetails')}
                disabled={!check?.extra_data || check?.extra_data?.length === 0}
                onClick={() => setShowExtraDetails(true)}
              />
              <ActionsMenu.Item
                icon="x-circle"
                title={t('common.cancel')}
                disabled={check.canceled}
                onClick={() => setCancelConfirmation(true)}
              />
              <ActionsMenu.Item
                icon="rotate-ccw"
                title={t('common.restore')}
                disabled={!check.canceled}
                onClick={() => setRestoreConfirmation(true)}
              />
              {checkType?.provider_form_class && (
                <>
                  <ActionsMenu.Item
                    icon="download"
                    title={t('admin.candidate.check.downloadConsentForm')}
                    disabled={!check.submitted_at}
                    onClick={() => handleDownloadProviderForm()}
                  />
                  {checkType?.provider_form_class === 'GenericProviderConsentForm' && (
                    <ActionsMenu.Item
                      icon="download"
                      title={t('admin.candidate.check.downloadConsentFormWetSignature')}
                      disabled={!check.submitted_at}
                      onClick={() => handleDownloadProviderForm(true)}
                    />
                  )}
                </>
              )}
              <ActionsMenu.Item
                icon="refresh-cw"
                title={t('admin.candidate.check.regenerateSubChecks')}
                disabled={!checkType?.has_subchecks}
                onClick={handleRegenerateSubChecks}
              />
              <ActionsMenu.Item
                icon="plus-square"
                title={t('admin.candidate.check.addSubChecks')}
                disabled={!checkType?.has_subchecks}
                onClick={() => setShowNewSubCheckModal(true)}
              />
              {!checkType?.has_subchecks && (
                <>
                  <ActionsMenu.Item
                    icon="upload"
                    title={t('admin.candidate.check.uploadSupportingDocument')}
                    disabled={checkType?.has_subchecks}
                    onClick={() => setShowCheckDocumentUploadModal(true)}
                  />
                  <ActionsMenu.Item
                    icon="refresh-cw"
                    title={t('admin.candidate.check.generateReport')}
                    disabled={checkType?.has_subchecks}
                    onClick={handleGenerateReport}
                  />
                  <ActionsMenu.Item
                    icon="file-minus"
                    title={t('admin.candidate.check.generateReportWithoutAttachments')}
                    disabled={checkType?.has_subchecks}
                    onClick={() => handleGenerateReport({}, true)}
                  />
                  <ActionsMenu.Item
                    icon="upload-cloud"
                    title={t('admin.candidate.check.sendPartnerWebhook')}
                    onClick={handleSendPartnerWebhook}
                  />
                </>
              )}
              {checkType?.has_subchecks && (
                <ActionsMenu.Item
                  icon="refresh-cw"
                  title={t('admin.candidate.check.generateSubchecksCombinedReport')}
                  disabled={!checkType?.has_subchecks}
                  onClick={() => handleGenerateSubchecksCombinedReport()}
                />
              )}
              {check?.upload_id && (
                <ActionsMenu.Item
                  icon="lock"
                  title={t('admin.candidate.check.blockResultUpload')}
                  disabled={check?.result_upload?.blocked}
                  onClick={handleBlockResultUpload}
                />
              )}
            </ActionsMenu>
          </div>
        </div>
        <div className="d-flex flex-column u-gap u-padding--small u-border-radius u-border surface-tertiary">
          <div className="d-flex flex-column u-gap-medium surface-white u-padding u-border-radius--x-small u-border">
            <div className="d-flex align-items-center u-gap-medium">
              <IconComponent icon={getCheckTypeIcon(checkType?.slug)} />
              <div className="d-flex flex-column">
                <span className="u-font-size-x-small u-font-weight-400 secondary-text">
                  {check?.id} {check?.provider_id && ` - (${check?.provider_id})`}
                </span>
                <span className="u-font-size-large u-font-weight-500 primary-text">
                  {checkType?.title}
                </span>
              </div>
            </div>
            {ageWarning && (
              <FormMessage
                message={ageWarning}
                type={MessageType.Error}
                closeable={false}
                noMargin={true}
              />
            )}
            {significantNote && (
              <FormMessage
                message={significantNote}
                type={MessageType.Info}
                closeable={false}
                noMargin={true}
              />
            )}
            {showProcessingInfo && (
              <CollapsibleBlock
                variant={CollapsibleBlockVariant.Neutral}
                startOpen={false}
                className="u-border-radius--medium">
                <CollapsibleBlock.Header>
                  <div className="d-flex align-items-center u-gap-small">
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {t('admin.candidate.check.processingInstructions', 'Processing Instructions')}
                    </span>
                    <div className="d-flex align-items-center u-gap-small">
                      {clientSpecificInstructions && (
                        <StatusPill
                          text={t('admin.candidate.check.clientSpecific', 'Client Specific')}
                          status="error"
                        />
                      )}
                      {hasAlternativeReport && (
                        <StatusPill
                          text={t('admin.candidate.check.alternativeReport', 'Alternative Report')}
                          status="error"
                        />
                      )}
                    </div>
                  </div>
                </CollapsibleBlock.Header>
                <CollapsibleBlock.Content>
                  <div className="u-padding u-border-radius--medium">
                    {clientSpecificInstructions && (
                      <p className="u-margin-bottom--small">
                        <span style={{ whiteSpace: 'pre-wrap' }}>{clientSpecificInstructions}</span>
                      </p>
                    )}
                    {hasAlternativeReport && (
                      <p>
                        <strong>{t('admin.candidate.check.alternativeReportEnabled')}</strong>
                      </p>
                    )}
                  </div>
                </CollapsibleBlock.Content>
              </CollapsibleBlock>
            )}
            {checkType?.has_id_requirements && (
              <div className="d-flex flex-column u-gap-medium">
                <FormMessage
                  message={
                    check?.id_requirements_met
                      ? t('admin.candidate.check.idRequirementsMetDescription')
                      : t('admin.candidate.check.idsNeedAttentionDescription')
                  }
                  type={check?.id_requirements_met ? MessageType.Success : MessageType.Error}
                  closeable={false}
                  noMargin={true}
                />
                <ToggleSwitch
                  label={
                    check?.id_requirements_met
                      ? t('admin.candidate.check.idRequirementsMet')
                      : t('admin.candidate.check.idsNeedAttention')
                  }
                  name="id_requirements_met"
                  inputProps={{
                    checked: !!check?.id_requirements_met,
                    onChange: e => handleUpdate({ id_requirements_met: e.target.checked }),
                  }}
                  status={
                    check?.id_requirements_met
                      ? { text: t('admin.candidate.check.requirementsMet'), status: 'success' }
                      : { text: t('admin.candidate.check.needsAttention'), status: 'error' }
                  }
                />
              </div>
            )}
            <div className="d-flex flex-column u-gap-small">
              <span className="t-tiny u-semi-bold u-uppercase secondary-text">
                In progress status
              </span>
              <Select
                value={check?.in_progress_sub_status}
                inputProps={{
                  onChange: e => handleUpdate({ in_progress_sub_status: e.target.value }),
                }}>
                <Select.Item value="processing">{t('common.processing')}</Select.Item>
                <Select.Item value="waiting_on_client">
                  {t('admin.candidate.check.waitingOnClient')}
                </Select.Item>
                <Select.Item value="waiting_on_candidate">
                  {t('admin.candidate.check.waitingOnCandidate')}
                </Select.Item>
                <Select.Item value="waiting_on_provider">
                  {t('admin.candidate.check.waitingOnProvider')}
                </Select.Item>
                <Select.Item value="blocked">{t('common.blocked')}</Select.Item>
                <Select.Item value="deferred">{t('common.deferred')}</Select.Item>
              </Select>
            </div>
            <div className={cn('d-flex', 'justify-content-between', 'align-items-center')}>
              <div className={cn(styles.candidateCheckRow)}>
                <RowDetails className="u-gap-small d-flex flex-wrap">
                  <RowDetails.Column
                    title={t('common.createdOn')}
                    data={<Moment format="DD/MM/YYYY">{check.created_at}</Moment>}
                  />
                  <RowDetails.Column
                    title={t('common.submittedAt')}
                    data={<Moment format="DD/MM/YYYY">{check.submitted_at}</Moment>}
                  />
                  <RowDetails.Column
                    title={t('common.completedOn')}
                    data={
                      check.completed_at ? (
                        <Moment format="DD/MM/YYYY">{check.completed_at}</Moment>
                      ) : (
                        '-'
                      )
                    }
                  />
                  {checkType?.has_job && checkType?.has_provider_id && (
                    <RowDetails.Column
                      title="Provider ID"
                      data={check.provider_id ? check.provider_id : '-'}
                    />
                  )}
                </RowDetails>
              </div>
            </div>
          </div>
          <div className="d-flex flex-column u-gap u-margin-top">
            {check?.sub_checks?.map(subCheck => (
              <SubCheckDetails
                key={subCheck.id}
                subCheck={getSubCheck(subCheck)}
                check={check}
                checkType={checkType}
                candidate={candidate}
                company={company}
                companyChecks={companyChecks}
                supportNotes={supportNotes}
                checkValues={checkValues}
                addOrUpdateCheck={addOrUpdateCheck}
                updateSubCheck={updateSubCheck}
                deleteSubCheck={deleteSubCheck}
                setLoading={setLoading}
                handleGenerateReport={handleGenerateReport}
                handleCreateCheckHistory={handleCreateCheckHistory}
                handleDeleteCheckHistory={handleDeleteCheckHistory}
                handleUpdateCheckHistory={handleUpdateCheckHistory}
                createDocumentRequest={createDocumentRequest}
              />
            ))}
          </div>
          <div className="d-flex flex-column u-gap-medium">
            {check.submitted_at && (
              <div className="d-flex u-margin-top flex-grow-1 u-gap-medium">
                <div className="d-flex flex-column u-width-100">
                  <span className="t-tiny u-semi-bold u-uppercase secondary-text">
                    Support User
                  </span>
                  <MultipleSelect
                    value={selectedSupportUsers}
                    onChange={handleUpdateSupportUser}
                    placeholder="Select support users"
                    useSearch>
                    {(supportUsers || []).map(user => (
                      <MultipleSelect.Item
                        key={user?.id}
                        value={user?.id}
                        active={selectedSupportUsers?.includes(user?.id)}>
                        {`${user?.first_name} ${user?.last_name}`}
                      </MultipleSelect.Item>
                    ))}
                  </MultipleSelect>
                </div>
              </div>
            )}
            <div className="d-flex flex-column">
              <span className="t-tiny u-semi-bold u-uppercase secondary-text">Comments</span>
              <CollapsibleBlock startOpen={false}>
                <CollapsibleBlock.Header>
                  <div
                    className={cn(
                      styles.collapsibleBlockHeaderTitle,
                      'd-flex align-items-center u-gap',
                    )}>
                    <span className="u-font-size-small u-font-weight-500 primary-text">Notes</span>
                    {(() => {
                      const totalComments =
                        supportNotes?.filter(note => !note.sub_check_id)?.length || 0;
                      const unresolvedComments =
                        supportNotes?.filter(note => !note.sub_check_id && !note.resolved)
                          ?.length || 0;

                      return (
                        totalComments > 0 && (
                          <div className="d-flex u-gap-small">
                            <StatusPill
                              text={`${totalComments} comments`}
                              status="neutral"
                              className="u-margin-right--small"
                            />
                            {unresolvedComments > 0 && (
                              <StatusPill
                                text={`${unresolvedComments} unresolved`}
                                status="neutral"
                                className="u-margin-right--small"
                              />
                            )}
                          </div>
                        )
                      );
                    })()}
                  </div>
                </CollapsibleBlock.Header>
                <CollapsibleBlock.Content>
                  <div className="d-flex flex-column u-gap-large u-max-height-400 u-min-height-150 u-overflow-y--auto u-overflow-hidden">
                    {(supportNotes || []).filter(note => !note.sub_check_id).length > 0 ? (
                      (supportNotes || [])
                        .filter(note => !note.sub_check_id)
                        .map(note => {
                          const supportUserName = note?.user_name || 'Admin';
                          return (
                            <Comments
                              key={note?.id}
                              title={supportUserName}
                              caption={
                                <Moment format="hh:mm DD/MM/YYYY ">{note?.created_at}</Moment>
                              }
                              initials={supportUserName?.charAt(0).toUpperCase() || 'A'}
                              tooltip={supportUserName}
                              comment={note?.message}
                              tag={AdminHistoryTypes[note?.type]?.title}
                              tagColor={AdminHistoryTypes[note?.type]?.color}
                              tagValue={note?.type}
                              allowDelete
                              onDelete={() => handleDeleteCheckHistory({ id: note.id })}
                              allowEdit
                              onEdit={data => handleUpdateCheckHistory(data, note.id)}
                              tagOptions={AdminHistoryTypesList}
                              edited={note?.modified_at}
                              allowResolve={note?.type !== CheckHistoryTypes.AdminGeneral}
                              onResolve={() =>
                                handleUpdateCheckHistory({ resolved: true }, note.id)
                              }
                              resolved={note?.resolved}
                              onReopen={() =>
                                handleUpdateCheckHistory({ resolved: false }, note.id)
                              }
                            />
                          );
                        })
                    ) : (
                      <div className="text-muted text-center py-3">
                        No notes yet. Add a note below to track progress or communicate with the
                        team.
                      </div>
                    )}
                  </div>
                  <CommentBox
                    ref={commentBoxRef}
                    tags={AdminHistoryTypesList}
                    tagDefault={CheckHistoryTypes.AdminGeneral}
                    className="u-border-top"
                    onSubmit={({ message, tag }) =>
                      handleCreateCheckHistory({ message, type: tag })
                    }
                    actionLabel={t('common.addNote')}
                  />
                </CollapsibleBlock.Content>
              </CollapsibleBlock>
            </div>
          </div>
        </div>
        <CandidateCheckDocumentUploadModal
          candidate={candidate}
          check={check}
          checkType={checkType}
          visible={showCheckDocumentUploadModal}
          onClose={() => setShowCheckDocumentUploadModal(false)}
          onUpdate={onDocumentUpload}
        />
      </div>
    </div>
  );
};

const SubCheckDetails = ({
  check,
  subCheck,
  checkType,
  candidate = null,
  company = null,
  companyChecks = [],
  supportNotes = [],
  checkValues = {},
  addOrUpdateCheck = () => {},
  updateSubCheck = () => {},
  deleteSubCheck = () => {},
  setLoading = () => {},
  handleGenerateReport = () => {},
  handleCreateCheckHistory = () => {},
  handleDeleteCheckHistory = () => {},
  handleUpdateCheckHistory = () => {},
  createDocumentRequest = () => {},
}) => {
  const { t } = useTranslation();
  const [showSubCheckDetails, setShowSubCheckDetails] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [showDocumentRequestModal, setShowDocumentRequestModal] = useState(false);
  const subCheckCommentBoxRef = useRef(null);

  const setInProgress = () => {
    handleUpdate({ processed_at: now() });
  };

  const handleUpdate = async (data, generateReport = false) => {
    try {
      setLoading(true);
      const { data: updatedSubCheck } = await updateSubCheck({ id: subCheck.id, params: data });
      const subChecks = (check?.sub_checks || []).map(subCheck =>
        subCheck.id === updatedSubCheck.id ? updatedSubCheck : subCheck,
      );
      addOrUpdateCheck({ ...check, sub_checks: subChecks });

      if (generateReport) {
        await handleGenerateReport({ sub_check_id: subCheck.id });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    await deleteSubCheck(subCheck.id);
    addOrUpdateCheck({
      ...check,
      sub_checks: check.sub_checks.filter(sc => sc.id !== subCheck.id),
    });
    setLoading(false);
  };

  const removeSubCheckComplete = async () => {
    setLoading(true);

    const { data: updatedSubCheck } = await updateSubCheck({
      id: subCheck.id,
      params: {
        completed_at: null,
      },
    });

    if (subCheck?.upload_id) {
      await deleteUpload(subCheck.upload_id);
    }

    const updatedSubChecks = check.sub_checks.map(sc =>
      sc.id === updatedSubCheck.id ? updatedSubCheck : sc,
    );

    addOrUpdateCheck({ ...check, sub_checks: updatedSubChecks });

    setLoading(false);
  };

  const handleCreateSubCheckHistory = async ({ message, type }) => {
    await handleCreateCheckHistory({ message, sub_check_id: subCheck.id, type });
    if (subCheckCommentBoxRef.current?.reset) {
      subCheckCommentBoxRef.current.reset();
    }
  };

  const handleCreateDocumentRequest = async data => {
    setLoading(true);
    try {
      await createDocumentRequest({
        checkId: check.id,
        data,
      }).unwrap();

      setShowDocumentRequestModal(false);
      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Document request sent!"
            message={`Document request for "${data.document_name}" has been sent to the candidate.`}
            type="success"
          />
        ),
        { duration: 5000 },
      );
      return { success: true };
    } catch (error) {
      const errorMessage = error.data?.message || error.message || 'Unknown error occurred';
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card card-with-border u-padding d-flex flex-column u-gap-medium">
      <Alert
        show={showDeleteConfirmationModal}
        title={t('admin.candidate.check.subCheck.delete.title', { title: subCheck.title })}
        ok="Yes"
        cancel="No"
        onOk={handleDelete}
        onCancel={() => setShowDeleteConfirmationModal(false)}
      />
      <KeyValueModal
        visible={showSubCheckDetails}
        onClose={() => setShowSubCheckDetails(false)}
        title={t('admin.candidate.check.subCheckDetails')}
        data={subCheck.details}
      />

      <CandidateCompletionModal
        companyId={check?.company_id}
        company={company}
        check={check}
        checkType={checkType}
        companyCheck={companyChecks?.find(cc => cc.type === check.type)}
        subCheck={subCheck}
        candidate={candidate}
        candidateValues={checkValues?.values}
        visible={showCompletionModal}
        onUpdate={handleUpdate}
        onClose={() => setShowCompletionModal(false)}
      />

      <DocumentRequestModal
        visible={showDocumentRequestModal}
        check={check}
        checkType={checkType}
        subCheck={subCheck}
        onSubmit={handleCreateDocumentRequest}
        onClose={() => setShowDocumentRequestModal(false)}
      />
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex">
          <Badge className={cn(`u-status-${subCheck.status}`, 'u-margin-right')}>
            {subCheck.status}
          </Badge>
        </div>
        <div>
          <IconButton
            className="u-margin-right"
            icon="trash-2"
            tip={t('common.delete')}
            small
            onClick={() => setShowDeleteConfirmationModal(true)}
          />
          <IconButton
            prefix="fa"
            className="u-margin-right"
            icon="chalkboard-teacher"
            tip={t('admin.candidate.check.subCheckDetails')}
            small
            disabled={check.canceled}
            onClick={() => setShowSubCheckDetails(true)}
          />
          {check.status === 'in-progress' && (
            <IconButton
              prefix="fa"
              className="u-margin-right"
              icon="envelope"
              tip={t('admin.candidate.documentRequest.title')}
              small
              onClick={() => setShowDocumentRequestModal(true)}
            />
          )}
          <IconButton
            prefix="fa"
            className="u-margin-right"
            icon="spinner"
            tip={t('admin.candidate.check.setInProgress')}
            small
            disabled={subCheck.processed_at || subCheck.canceled || subCheck.completed_at}
            onClick={setInProgress}
          />
          <IconButton
            prefix="fa"
            className="u-margin-right"
            icon="undo"
            tip={t('common.undoCompletion')}
            small
            onClick={removeSubCheckComplete}
          />
          <IconButton
            prefix="fa"
            className="u-margin-right"
            icon="check"
            tip={t('common.complete')}
            small
            disabled={subCheck.canceled}
            onClick={() => setShowCompletionModal(true)}
          />
          {subCheck?.url && (
            <IconButton
              prefix="fa"
              icon="link"
              tip={t('common.openLink')}
              small
              onClick={() => window.open(subCheck.url, '_blank')}
            />
          )}
        </div>
      </div>
      {subCheck?.upload_id && (
        <div className="d-flex u-margin-top--small">
          <StatusPill
            text={`Result Upload ID: ${subCheck.upload_id}`}
            status="neutral"
            className={styles.status}
          />
        </div>
      )}
      <div className="d-flex flex-column">
        <span className="t-tiny u-semi-bold u-uppercase secondary-text">
          {t('common.subCheck')}
        </span>
        <span className="u-font-size-medium u-font-weight-500 primary-text">{subCheck.title}</span>
      </div>

      <div className="d-flex flex-column u-gap-small">
        <span className="t-tiny u-semi-bold u-uppercase secondary-text">In progress status</span>
        <Select
          rounded
          value={subCheck?.in_progress_sub_status}
          inputProps={{
            onChange: e => handleUpdate({ in_progress_sub_status: e.target.value }),
          }}>
          <Select.Item value="processing">{t('common.processing')}</Select.Item>
          <Select.Item value="waiting_on_client">
            {t('admin.candidate.check.waitingOnClient')}
          </Select.Item>
          <Select.Item value="waiting_on_candidate">
            {t('admin.candidate.check.waitingOnCandidate')}
          </Select.Item>
          <Select.Item value="waiting_on_provider">
            {t('admin.candidate.check.waitingOnProvider')}
          </Select.Item>
          <Select.Item value="blocked">{t('common.blocked')}</Select.Item>
          <Select.Item value="deferred">{t('common.deferred')}</Select.Item>
        </Select>
      </div>

      <CollapsibleBlock startOpen={false}>
        <CollapsibleBlock.Header>
          <div
            className={cn(styles.collapsibleBlockHeaderTitle, 'd-flex align-items-center u-gap')}>
            <span className="u-font-size-small u-font-weight-500 primary-text">Notes</span>
            {(() => {
              const totalComments =
                (supportNotes || []).filter(note => note.sub_check_id === subCheck.id)?.length || 0;
              const unresolvedComments =
                (supportNotes || []).filter(
                  note => note.sub_check_id === subCheck.id && !note.resolved,
                )?.length || 0;

              return (
                totalComments > 0 && (
                  <div className="d-flex u-gap-small">
                    <StatusPill
                      text={`${totalComments} comments`}
                      status="neutral"
                      className="u-margin-right--small"
                    />
                    {unresolvedComments > 0 && (
                      <StatusPill
                        text={`${unresolvedComments} unresolved`}
                        status="neutral"
                        className="u-margin-right--small"
                      />
                    )}
                  </div>
                )
              );
            })()}
          </div>
        </CollapsibleBlock.Header>
        <CollapsibleBlock.Content>
          <div className="d-flex flex-column u-padding--small u-gap-large">
            <div className="d-flex flex-column u-gap-large u-max-height-400 u-min-height-150 u-overflow-y--auto u-overflow-hidden">
              {(supportNotes || []).filter(note => note.sub_check_id === subCheck.id).length > 0 ? (
                (supportNotes || [])
                  .filter(note => note.sub_check_id === subCheck.id)
                  .map(note => {
                    const supportUserName = note?.user_name || 'Admin';
                    return (
                      <Comments
                        key={note?.id}
                        title={supportUserName}
                        caption={<Moment format="hh:mm DD/MM/YYYY ">{note?.created_at}</Moment>}
                        initials={supportUserName?.charAt(0).toUpperCase() || 'A'}
                        tooltip={supportUserName}
                        comment={note?.message}
                        tag={AdminHistoryTypes[note?.type]?.title}
                        tagColor={AdminHistoryTypes[note?.type]?.color}
                        tagValue={note?.type}
                        allowDelete
                        allowEdit
                        onEdit={data => handleUpdateCheckHistory(data, note.id)}
                        onDelete={() => handleDeleteCheckHistory({ id: note.id })}
                        tagOptions={AdminHistoryTypesList}
                        edited={note?.modified_at}
                        allowResolve={note?.type !== CheckHistoryTypes.AdminGeneral}
                        onResolve={() => handleUpdateCheckHistory({ resolved: true }, note.id)}
                        resolved={note?.resolved}
                        onReopen={() => handleUpdateCheckHistory({ resolved: false }, note.id)}
                      />
                    );
                  })
              ) : (
                <div className="text-muted text-center py-3">
                  No notes yet. Add a note below to track progress or communicate with the team.
                </div>
              )}
            </div>
            <CommentBox
              ref={subCheckCommentBoxRef}
              tags={AdminHistoryTypesList}
              tagDefault={CheckHistoryTypes.AdminGeneral}
              className="u-border-top"
              onSubmit={({ message, tag }) => handleCreateSubCheckHistory({ message, type: tag })}
              actionLabel={t('common.addNote')}
            />
          </div>
        </CollapsibleBlock.Content>
      </CollapsibleBlock>
    </div>
  );
};
